import { useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import type { TestType } from '@alpha-edge/shared'
import { useApiList } from '../../hooks/use-api-list'
import { useDebounce } from '../../hooks/use-debounce'
import { useAuth } from '../../hooks/use-auth'
import {
  PageHeader,
  DataTable,
  Pagination,
  StatusBadge,
  ColumnSettingsButton,
  btnPrimary,
  btnSecondary,
  input,
  colors,
} from '../../components/ui'
import type { Column } from '../../components/ui'
import { BulkImportModal } from '../../components/BulkImportModal'
import { getAuthToken } from '../../services/api'
import { useTableColumns } from '../../hooks/use-table-columns'

const columns: readonly Column<TestType>[] = [
  { key: 'code', header: 'Code', render: (t) => <strong>{t.code}</strong> },
  { key: 'name', header: 'Name', render: (t) => t.name },
  { key: 'category', header: 'Category', render: (t) => t.category || '--' },
  { key: 'specimen', header: 'Specimen', render: (t) => t.specimenType || '--' },
  { key: 'loinc', header: 'LOINC', render: (t) => t.loincCode || '--' },
  { key: 'status', header: 'Status', render: (t) => <StatusBadge status={t.isActive ? 'active' : 'inactive'} /> },
]

export function TestTypeListPage() {
  const navigate = useNavigate()
  const { user } = useAuth()
  const [search, setSearch] = useState('')
  const debouncedSearch = useDebounce(search)
  const { data, total, page, loading, setPage, refresh } = useApiList<TestType>({
    path: '/test-types',
    filters: { search: debouncedSearch || undefined },
  })

  const [importModalOpen, setImportModalOpen] = useState(false)
  const { tableId: _, ...tableColumnProps } = useTableColumns('test-types')

  const handleExportCsv = useCallback(() => {
    const params = new URLSearchParams({ format: 'csv' })
    if (debouncedSearch) params.set('search', debouncedSearch)
    const url = `/api/bulk/test_type/export?${params.toString()}`
    const token = getAuthToken()
    void fetch(url, {
      headers: token ? { Authorization: `Bearer ${token}` } : {},
    }).then(async (res) => {
      if (!res.ok) return
      const blob = await res.blob()
      const disposition = res.headers.get('Content-Disposition') ?? ''
      const match = /filename="([^"]+)"/.exec(disposition)
      const filename = match ? match[1] : `test_type-${new Date().toISOString().slice(0, 10)}.csv`
      const objectUrl = URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = objectUrl
      link.download = filename
      link.click()
      URL.revokeObjectURL(objectUrl)
    })
  }, [debouncedSearch])

  return (
    <div>
      <PageHeader
        title="Test Types"
        subtitle={`${total} total`}
        actions={
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <ColumnSettingsButton columns={columns} {...tableColumnProps} />
            <button onClick={() => navigate('/test-types/new')} style={btnPrimary}>
              Add Test Type
            </button>
            {user?.role !== 'tech' && (
              <button type="button" onClick={() => setImportModalOpen(true)} style={btnSecondary}>
                Import CSV
              </button>
            )}
            <button type="button" onClick={handleExportCsv} style={btnSecondary}>
              Export CSV
            </button>
          </div>
        }
      />
      <div style={{ marginBottom: '1rem' }}>
        <input
          style={{ ...input, width: '300px' }}
          placeholder="Search by name..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>
      {loading ? (
        <div style={{ color: colors.textMuted }}>Loading...</div>
      ) : (
        <>
          <DataTable
            columns={columns}
            rows={data}
            keyField="id"
            onRowClick={(t) => navigate(`/test-types/${t.id}`)}
            emptyMessage="No test types found."
            {...tableColumnProps}
          />
          <Pagination page={page} total={total} limit={25} onPageChange={setPage} />
        </>
      )}

      <BulkImportModal
        isOpen={importModalOpen}
        onClose={() => setImportModalOpen(false)}
        entityType="test_type"
        entityLabel="Test Types"
        onSuccess={refresh}
      />
    </div>
  )
}
